import { useState } from "react";
import { Play, Clock, Upload, Mic, FileText, ChevronRight, Check, Edit, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Lead } from "@/types";
import { startCampaign, simulateCampaignResults, startRealCampaign } from "@/services/campaigns";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LeadForm } from "@/components/LeadForm";
import { CSVUploader } from "@/components/CSVUploader";

const mockVoices = [
  {
    id: "voice-uuid-123",
    name: "Emma",
    provider: "ElevenLabs",
    description: "Warm and conversational",
    audio_preview_url: "https://example.com/emma-preview.mp3"
  },
  {
    id: "voice-uuid-124",
    name: "James",
    provider: "ElevenLabs",
    description: "Professional and trustworthy",
    audio_preview_url: "https://example.com/james-preview.mp3"
  }
];

const mockPromptTemplates = [
  {
    id: "prompt-uuid-001",
    title: "Welcome Script v2",
    category: "welcome",
    content: "Hi! Just checking in to see if you're still in the market for a vehicle.",
    do_say_guidelines: "Be warm, direct, and informative",
    dont_say_guidelines: "Don't promise anything or sound robotic"
  },
  {
    id: "prompt-uuid-002",
    title: "Follow-up Script",
    category: "followup",
    content: "Hello again! I wanted to follow up on our previous conversation about your vehicle needs.",
    do_say_guidelines: "Reference previous contact, be helpful",
    dont_say_guidelines: "Don't be pushy or make assumptions"
  }
];

interface QueuedLeadsCampaignProps {
  leads: Lead[];
  refreshLeads: () => Promise<void>;
  showLeadUploadModal?: boolean;
  setShowLeadUploadModal?: (show: boolean) => void;
}

const QueuedLeadsCampaign = ({ 
  leads, 
  refreshLeads, 
  showLeadUploadModal = false, 
  setShowLeadUploadModal 
}: QueuedLeadsCampaignProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(mockVoices[0]);
  const [selectedPrompt, setSelectedPrompt] = useState(mockPromptTemplates[0]);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [showPromptSelector, setShowPromptSelector] = useState(false);
  const [internalShowLeadUploadModal, setInternalShowLeadUploadModal] = useState(false);

  const queuedLeads = leads.filter(lead => lead.status === "queued");
  const hasQueuedLeads = queuedLeads.length > 0;
  
  const actualShowLeadUploadModal = setShowLeadUploadModal ? showLeadUploadModal : internalShowLeadUploadModal;
  const actualSetShowLeadUploadModal = setShowLeadUploadModal || setInternalShowLeadUploadModal;

  const handleStartCampaign = async (demo: boolean = false) => {
    setIsStarting(true);

    try {
      const leadIds = queuedLeads.map(lead => lead.id).filter(Boolean) as string[];
      let result;
      
      if (demo) {
        const { success, error } = await startCampaign(leadIds);
        if (!success) throw new Error(error);
        
        result = await simulateCampaignResults(leadIds);
      } else {
        const selectedLeads = queuedLeads.filter(lead => lead.id && leadIds.includes(lead.id));
        
        result = await startRealCampaign(
          selectedLeads,
          selectedPrompt.content,
          selectedVoice.id
        );
      }

      if (result.success) {
        toast({
          title: demo ? "Demo reheating completed" : "Reheating started",
          description: demo 
            ? "Demo results have been generated for the selected leads." 
            : "The reheating campaign has been started for the selected leads.",
        });
        refreshLeads();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Campaign error:", error);
      toast({
        title: "Reheating error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsStarting(false);
      setShowCampaignDialog(false);
    }
  };

  const VoiceSelectorTrigger = ({ children }: { children: React.ReactNode }) => {
    if (isMobile) {
      return (
        <Sheet open={showVoiceSelector} onOpenChange={setShowVoiceSelector}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" onClick={() => setShowVoiceSelector(true)}>
              {children}
            </Button>
          </SheetTrigger>
        </Sheet>
      );
    }
    return (
      <Dialog open={showVoiceSelector} onOpenChange={setShowVoiceSelector}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" onClick={() => setShowVoiceSelector(true)}>
            {children}
          </Button>
        </DialogTrigger>
      </Dialog>
    );
  };

  const PromptSelectorTrigger = ({ children }: { children: React.ReactNode }) => {
    if (isMobile) {
      return (
        <Sheet open={showPromptSelector} onOpenChange={setShowPromptSelector}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" onClick={() => setShowPromptSelector(true)}>
              {children}
            </Button>
          </SheetTrigger>
        </Sheet>
      );
    }
    return (
      <Dialog open={showPromptSelector} onOpenChange={setShowPromptSelector}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" onClick={() => setShowPromptSelector(true)}>
            {children}
          </Button>
        </DialogTrigger>
      </Dialog>
    );
  };

  const renderVoiceSelector = () => {
    if (isMobile) {
      return (
        <Sheet open={showVoiceSelector} onOpenChange={setShowVoiceSelector}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Select Voice</SheetTitle>
              <SheetDescription>
                Choose the voice that will speak to your leads
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-4 mt-6">
              {mockVoices.map((voice) => (
                <div 
                  key={voice.id}
                  className={`p-4 rounded-lg border cursor-pointer ${selectedVoice.id === voice.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                  onClick={() => setSelectedVoice(voice)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">{voice.name}</h3>
                      <p className="text-sm text-muted-foreground">{voice.description}</p>
                    </div>
                    {selectedVoice.id === voice.id && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div className="mt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        toast({
                          title: "Audio preview",
                          description: `Playing ${voice.name} voice sample...`,
                        });
                      }}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Button 
                className="w-full" 
                onClick={() => setShowVoiceSelector(false)}
              >
                Confirm Selection
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      );
    }
    
    return (
      <Dialog open={showVoiceSelector} onOpenChange={setShowVoiceSelector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Voice</DialogTitle>
            <DialogDescription>
              Choose the voice that will speak to your leads
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-6">
            {mockVoices.map((voice) => (
              <div 
                key={voice.id}
                className={`p-4 rounded-lg border cursor-pointer ${selectedVoice.id === voice.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                onClick={() => setSelectedVoice(voice)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{voice.name}</h3>
                    <p className="text-sm text-muted-foreground">{voice.description}</p>
                  </div>
                  {selectedVoice.id === voice.id && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div className="mt-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      toast({
                        title: "Audio preview",
                        description: `Playing ${voice.name} voice sample...`,
                      });
                    }}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button 
              className="w-full" 
              onClick={() => setShowVoiceSelector(false)}
            >
              Confirm Selection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const renderPromptSelector = () => {
    if (isMobile) {
      return (
        <Sheet open={showPromptSelector} onOpenChange={setShowPromptSelector}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Select Call Script</SheetTitle>
              <SheetDescription>
                Choose what the AI will say to your leads
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-4 mt-6">
              {mockPromptTemplates.map((prompt) => (
                <div 
                  key={prompt.id}
                  className={`p-4 rounded-lg border cursor-pointer ${selectedPrompt.id === prompt.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                  onClick={() => setSelectedPrompt(prompt)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">{prompt.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{prompt.category}</p>
                    </div>
                    {selectedPrompt.id === prompt.id && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div className="mt-2 p-3 bg-muted/50 rounded-md text-sm">
                    "{prompt.content}"
                  </div>
                  <Accordion type="single" collapsible className="mt-3">
                    <AccordionItem value="guidelines">
                      <AccordionTrigger className="text-sm py-2">View Guidelines</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 text-sm">
                          <p className="font-medium">Do:</p>
                          <p className="text-muted-foreground">{prompt.do_say_guidelines}</p>
                          <p className="font-medium mt-2">Don't:</p>
                          <p className="text-muted-foreground">{prompt.dont_say_guidelines}</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Button 
                className="w-full" 
                onClick={() => setShowPromptSelector(false)}
              >
                Confirm Selection
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      );
    }
    
    return (
      <Dialog open={showPromptSelector} onOpenChange={setShowPromptSelector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Call Script</DialogTitle>
            <DialogDescription>
              Choose what the AI will say to your leads
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-6">
            {mockPromptTemplates.map((prompt) => (
              <div 
                key={prompt.id}
                className={`p-4 rounded-lg border cursor-pointer ${selectedPrompt.id === prompt.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                onClick={() => setSelectedPrompt(prompt)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{prompt.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{prompt.category}</p>
                  </div>
                  {selectedPrompt.id === prompt.id && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div className="mt-2 p-3 bg-muted/50 rounded-md text-sm">
                  "{prompt.content}"
                </div>
                <Accordion type="single" collapsible className="mt-3">
                  <AccordionItem value="guidelines">
                    <AccordionTrigger className="text-sm py-2">View Guidelines</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 text-sm">
                        <p className="font-medium">Do:</p>
                        <p className="text-muted-foreground">{prompt.do_say_guidelines}</p>
                        <p className="font-medium mt-2">Don't:</p>
                        <p className="text-muted-foreground">{prompt.dont_say_guidelines}</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button 
              className="w-full" 
              onClick={() => setShowPromptSelector(false)}
            >
              Confirm Selection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="mb-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Clock className="mr-2 h-5 w-5 text-primary" />
        Reheat Your Leads
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={hasQueuedLeads ? "bg-primary/5 border-primary/20" : ""}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                1
              </div>
              Upload Leads
              {hasQueuedLeads && <Check className="h-4 w-4 text-green-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-2 text-sm">
            <p className="text-muted-foreground">
              {hasQueuedLeads 
                ? `${queuedLeads.length} lead${queuedLeads.length !== 1 ? 's' : ''} ready`
                : "Import your cold leads"
              }
            </p>
          </CardContent>
          <CardFooter>
            <Button 
              variant={hasQueuedLeads ? "outline" : "default"}
              size="sm" 
              className="w-full"
              onClick={() => actualSetShowLeadUploadModal(true)}
            >
              {hasQueuedLeads ? "Edit Leads" : "Upload Leads"}
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                2
              </div>
              Voice
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-2 text-sm">
            <p className="text-muted-foreground">
              {selectedVoice.name} – {selectedVoice.description}
            </p>
          </CardContent>
          <CardFooter>
            <VoiceSelectorTrigger>Edit Voice</VoiceSelectorTrigger>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                3
              </div>
              Call Script
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-2 text-sm">
            <p className="text-muted-foreground">
              {selectedPrompt.title}
            </p>
          </CardContent>
          <CardFooter>
            <PromptSelectorTrigger>Edit Script</PromptSelectorTrigger>
          </CardFooter>
        </Card>

        <Card className="bg-primary/5 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                4
              </div>
              Launch
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-2 text-sm">
            <p className="text-muted-foreground">
              {hasQueuedLeads 
                ? `Reheat ${queuedLeads.length} lead${queuedLeads.length !== 1 ? 's' : ''}`
                : "Upload leads first"
              }
            </p>
          </CardContent>
          <CardFooter>
            <Button
              onClick={() => setShowCampaignDialog(true)}
              className="w-full gap-2"
              size="sm"
              disabled={!hasQueuedLeads}
            >
              <Play className="h-4 w-4" />
              Reheat Leads
            </Button>
          </CardFooter>
        </Card>
      </div>

      {renderVoiceSelector()}

      {renderPromptSelector()}

      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Reheating Campaign</DialogTitle>
            <DialogDescription>
              Start a calling campaign for {queuedLeads.length} queued lead{queuedLeads.length !== 1 ? 's' : ''}. 
              Calls will be processed in the background.
            </DialogDescription>
          </DialogHeader>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Choose mode</AlertTitle>
            <AlertDescription>
              You can run a real campaign or a demo campaign with simulated results.
            </AlertDescription>
          </Alert>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCampaignDialog(false)}
              disabled={isStarting}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleStartCampaign(true)}
              disabled={isStarting}
              className="gap-2"
            >
              <PlayCircle className="h-4 w-4" />
              Run Demo Campaign
            </Button>
            <Button
              onClick={() => handleStartCampaign(false)}
              disabled={isStarting}
              className="gap-2"
            >
              <Play className="h-4 w-4" />
              Start Real Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog 
        open={actualShowLeadUploadModal} 
        onOpenChange={actualSetShowLeadUploadModal}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload or Edit Leads</DialogTitle>
            <DialogDescription>
              Add new leads or manage your existing ones
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="manual" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="manual">Manual Entry</TabsTrigger>
              <TabsTrigger value="csv">CSV Upload</TabsTrigger>
            </TabsList>
            
            <TabsContent value="manual">
              <LeadForm onComplete={() => {
                actualSetShowLeadUploadModal(false);
                refreshLeads();
              }} />
            </TabsContent>
            
            <TabsContent value="csv">
              <CSVUploader />
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => actualSetShowLeadUploadModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QueuedLeadsCampaign;
